import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { Download, Search, User, XCircle } from 'react-feather';
import { addDays, subDays } from 'date-fns';
import { memo, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import { GetEventRegistrationsStatsQuery as GetEventRegistrationsStats } from '__generated__/graphql';

import { FilterIndicator } from './Layout';
import { assetUrl, encodeUrl } from '../../common/helpers';
import { useColors } from './helpers';
import { useCompareEventsModal } from './CompareEventsModal';
import UI from '../../common/UI';
import useDashboardUrlState from './useDashboardUrlState';
import useLocale from '../../common/useLocale';
import useProject from '../useProject';

type RegistrationCounts = GetEventRegistrationsStats['registrationCounts'][0];
type Event = RegistrationCounts['event'];
type Ticket = Event['tickets'][0];

interface DataPoint {
  x: number;
  [eventIndex: number]: number;
}

interface ActivePlot {
  event: Event;
  tickets: Ticket[];
}

interface RegistrationsGraphProps {
  eventId: string;
  data: RegistrationCounts[];
}

const RegistrationsGraph = memo(({ eventId, data }: RegistrationsGraphProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatDate, parseDate } = useLocale();
  const project = useProject();
  const theme: any = useTheme();

  const [urlState, setUrlState] = useDashboardUrlState();
  const [updating, setUpdating] = useState(false);

  const comparedEventsChecksum = JSON.stringify(urlState.comparedEvents);

  useEffect(() => {
    // When the URL changes, expect the data to change, start loading
    setUpdating(true);
  }, [comparedEventsChecksum]);

  useEffect(() => {
    // Data has changed, loading is complete
    setUpdating(false);
  }, [data]);

  const unsortedGraphData: DataPoint[] = [];
  const pointsMap: { [x: number]: DataPoint; } = {};
  let yMax = 0;

  // Convert the registration counts to an array that looks as follows:
  // [{ x: -5, 0: 78, 1: 55, 2: 18 }]
  // Here x is the number of days before the event and 0, 1, and 2 are
  // the keys of the events that point to the number of registratrions.
  data.forEach((eventData, eventIndex) => {
    eventData.counts.forEach((count, index) => {
      const x = eventData.days[index];

      const point = pointsMap[x] || {
        x,
      };

      point[eventIndex] = count;

      if (!(x in pointsMap)) {
        pointsMap[x] = point;
        unsortedGraphData.push(point);
      }

      if (count > yMax) {
        yMax = count;
      }
    });
  });

  const graphData = sortBy(unsortedGraphData, 'x');
  const xMin = graphData[0]?.x || 0;
  const xMax = graphData[graphData.length - 1]?.x || 0;

  const events = data.map(({ event }) => event);

  /**
   * A list of objects containing the event and ticket filters.
   */
  const activePlots: ActivePlot[] = [
    {
      event: events[0],
      tickets: events[0].tickets.filter((ticket) => urlState.tickets.includes(ticket.id)),
    },
    ...urlState.comparedEvents
      .map((param) => {
        const event = events.filter((event) => event.id === param.event)[0];

        if (event) {
          return {
            event,
            tickets: event.tickets.filter((ticket) => (param.tickets || []).includes(ticket.id)),
          };
        }

        return null;
      })
      .filter((plot) => plot),
  ];

  const formatXTick = (day: number) => {
    const startDates = uniq(events.map((event) => event.start_date));

    if (startDates.length === 1) {
      const startDate = parseDate(startDates[0], { timezone: 'UTC' });

      return formatDate(addDays(startDate, day), { format: 'display_date' });
    }

    return t('t_minus_n', { n: formatInteger(Math.abs(day)) });
  };

  const colors = useColors();

  const [openModal] = useCompareEventsModal({
    event: events[0],
    colors,
  });

  const removeEvent = (index: number) => {
    setUrlState((urlState) => {
      const comparedEvents = [...urlState.comparedEvents];
      comparedEvents.splice(index, 1);

      return {
        ...urlState,
        comparedEvents,
      };
    });
  };

  return (
    <UI.Card>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <UI.FlexContainer sc={{ alignItems: 'center' }}>
          <UI.H4>
            {t('number_of_registrations')}
          </UI.H4>
          <FilterIndicator sc={{ ml: 2 }} alignV="bottom" />
        </UI.FlexContainer>
        <UI.FlexContainer sc={{ alignItems: 'center' }}>
          {activePlots.length > 1 && activePlots.map(({ event }, index) => (
            <UI.FlexContainer sc={{ alignItems: 'center', ml: 3 }} key={index}>
              <UI.Span
                sc={{ fontWeight: 500, small: true, pr: index === 0 ? 1 : 0, inlineBlock: true }}
                style={{ color: colors[index], maxWidth: activePlots.length >= 3 ? 200 : 300 }}
              >
                <UI.Truncate>
                  {`${index + 1}. `}
                  {event.title}
                </UI.Truncate>
              </UI.Span>
              {index > 0 && (
                <UI.Button
                  onClick={() => removeEvent(index - 1)}
                  sc={{
                    blank: true, size: 'xs', my: -1, px: 0.75,
                  }}
                >
                  <UI.Icon sc={{ color: 'gray.400' }}>
                    <XCircle />
                  </UI.Icon>
                </UI.Button>
              )}
            </UI.FlexContainer>
          ))}
          {graphData.length > 0 && (
            <>
              <UI.Button
                onClick={openModal}
                sc={{
                  blank: true,
                  size: 'xs',
                  my: -1,
                  ml: 3,
                  disabled: urlState.comparedEvents.length >= 2,
                  muted: updating,
                }}
              >
                <UI.Icon>
                  {updating ? <UI.Loader /> : <Search />}
                </UI.Icon>
                {' '}
                {t('compare')}
              </UI.Button>
              <UI.AButton
                href={`/b/api/analytics/registration_counts/${project.id}?${encodeUrl({ event: eventId, tickets: urlState.tickets })}`}
                target="_blank"
                rel="noopener noreferrer"
                sc={{
                  blank: true,
                  size: 'xs',
                  my: -1,
                  ml: 1,
                }}
              >
                <UI.Icon sc={{ mb: 0, mt: 0.25 }}>
                  <Download />
                </UI.Icon>
              </UI.AButton>
            </>
          )}
        </UI.FlexContainer>
      </UI.FlexContainer>

      <UI.Div style={{ height: 300 }}>
        {graphData.length > 0 && (
          <ResponsiveContainer>
            <LineChart
              height={300}
              data={graphData}
              margin={{ top: 10, right: 5, bottom: 10, left: 0 }}
            >
              <CartesianGrid
                stroke={theme.colors.gray[200]}
                strokeDasharray="5 5"
                vertical={false}
              />
              <XAxis
                dataKey="x"
                type="category"
                domain={[xMin, xMax]}
                tickFormatter={formatXTick}
                tick={{ fontSize: 10 }}
                stroke={theme.colors.gray[500]}
                dy={10}
              />
              <YAxis
                strokeWidth={0}
                tick={{ fontSize: 11 }}
                width={`${yMax}`.length * 6.4 + 18}
                allowDecimals={false}
              />
              <Tooltip
                content={(props: TooltipProps<number, number>) => (
                  <CustomTooltip colors={colors} activePlots={activePlots} content={props} />
                )}
              />
              {activePlots.map((plot, index) => (
                <Line
                  type="monotone"
                  dataKey={index}
                  stroke={colors[index]}
                  strokeWidth={2}
                  strokeDasharray={index > 0 ? '6 3' : undefined}
                  dot={false}
                  key={index}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
        {graphData.length === 0 && (
          <UI.FlexContainer
            sc={{ justifyContent: 'center', alignItems: 'center', direction: 'column' }}
            style={{ height: '100%' }}
          >
            <img
              src={assetUrl('images/dashboard-registrations.svg')}
              height={100}
              alt={t('registrations')}
              style={{ display: 'block' }}
            />
            <UI.Div sc={{ textAlign: 'center', color: 'gray.400', mt: 4 }}>
              {t('no_data_available')}
            </UI.Div>
          </UI.FlexContainer>
        )}
      </UI.Div>
    </UI.Card>
  );
});

interface CustomTooltipProps {
  colors: string[];
  activePlots: ActivePlot[];
  content: TooltipProps<number, number>;
}

const CustomTooltip = ({ colors, activePlots, content: { active, payload, label } }: CustomTooltipProps) => {
  const { t } = useTranslation();
  const { formatInteger, formatDate, parseDate } = useLocale();

  const payloads = sortBy(payload, ({ value }) => -value);

  const daysToGo = Math.abs(label);

  return active && payloads.length > 0 && (
    <UI.Card sc={{ padding: 2, active: true, small: true }} style={{ borderRadius: 8 }}>
      <UI.GridContainer sc={{ gutter: 0.25 }}>
        {payloads.map((payload, index) => {
          const { event } = activePlots[payload.name];

          return (
            <UI.Div key={index}>
              <UI.Strong sc={{ block: true }} style={{ color: colors[payload.name] }}>
                {`${payload.name + 1}. `}
                {event.title}
              </UI.Strong>
              <UI.Delimit>
                <>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {formatInteger(payload.value)}
                </>
                {formatDate(
                  subDays(parseDate(event.start_date, { timezone: 'UTC' }), daysToGo),
                  { format: 'display_date' },
                )}
              </UI.Delimit>
            </UI.Div>
          );
        })}
        <UI.Div sc={{ muted: true }}>
          {label <= 0 && t('n_days_before_event', { count: daysToGo })}
          {label > 0 && t('n_days_after_start_event', { count: daysToGo })}
        </UI.Div>
      </UI.GridContainer>
    </UI.Card>
  );
};

export default RegistrationsGraph;
